import React from 'react';
import { Check } from 'lucide-react';

/**
 * Horizontal step progress indicator
 * Shows workflow progress with completed, active, and pending states
 */
const StepProgress = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  maxAccessibleStep = 0,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(step.id);
          const isCurrent = currentStep === step.id;
          // A step is accessible if:
          // 1. It's step 0 (always accessible), OR
          // 2. The previous step is completed, OR
          // 3. It's within maxAccessibleStep (restored from plan state)
          const maxFromCompleted = completedSteps.size > 0 ? Math.max(...completedSteps) + 1 : 0;
          const maxAllowed = Math.max(maxFromCompleted, maxAccessibleStep);
          const isAccessible = step.id === 0 || step.id <= maxAllowed;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              {/* Step Circle and Label */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isAccessible && onStepClick && onStepClick(step.id)}
                  disabled={!isAccessible}
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    font-semibold text-sm transition-all duration-200
                    ${isCompleted
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : isCurrent
                        ? 'bg-blue-500 text-white ring-4 ring-blue-100'
                        : isAccessible
                          ? 'bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    step.id + 1
                  )}
                </button>
                <div className="mt-2 text-center">
                  <div className={`text-xs font-medium ${
                    isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </div>
                </div>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex-1 mx-2 h-1 rounded">
                  <div className={`h-full rounded transition-all duration-300 ${
                    completedSteps.has(step.id) ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Compact step progress for mobile or narrow layouts
 */
export const StepProgressCompact = ({
  steps,
  currentStep,
  completedSteps,
  className = ''
}) => {
  const currentStepData = steps.find(s => s.id === currentStep);
  const completedCount = completedSteps.size;

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex items-center">
        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-sm">
          {currentStep + 1}
        </div>
        <div className="ml-3">
          <div className="text-sm font-medium text-gray-900">
            {currentStepData?.title}
          </div>
          <div className="text-xs text-gray-500">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-1">
        {steps.map((step) => (
          <div
            key={step.id}
            className={`w-2 h-2 rounded-full ${
              completedSteps.has(step.id)
                ? 'bg-green-500'
                : currentStep === step.id
                  ? 'bg-blue-500'
                  : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default StepProgress;
